import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  margin: 0;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #4b5563;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1.2fr);
  gap: 1.5rem;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Panel = styled.section`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const PanelTitle = styled.h2`
  font-size: 1.1rem;
  margin: 0;
  color: #111827;
  font-weight: 600;
`;

const FieldLabel = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: #374151;
  display: block;
  margin-bottom: 0.25rem;
`;

const HelperText = styled.p`
  margin: 0.15rem 0 0;
  font-size: 0.8rem;
  color: #4b5563;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-size: 0.9rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-size: 0.9rem;
  resize: vertical;
`;

const SmallTextArea = styled(TextArea)`
  min-height: 60px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.25rem;
`;

const PrimaryButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 999px;
  border: none;
  background-color: #111827;
  color: #f9fafb;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.05s ease;

  &:hover {
    background-color: #1f2937;
    transform: translateY(-1px);
  }
`;

const SecondaryButton = styled.button`
  padding: 0.5rem 0.9rem;
  border-radius: 999px;
  border: 1px solid #d1d5db;
  background-color: #fff;
  color: #374151;
  font-size: 0.85rem;
  cursor: pointer;
`;

const ChatBubbleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const BubbleRow = styled.div`
  display: flex;
  justify-content: ${props => (props.$role === 'assistant' ? 'flex-start' : 'flex-end')};
`;

const Bubble = styled.div`
  max-width: 85%;
  padding: 0.6rem 0.8rem;
  border-radius: 14px;
  background-color: ${props => (props.$role === 'assistant' ? '#eff6ff' : '#111827')};
  color: ${props => (props.$role === 'assistant' ? '#111827' : '#f9fafb')};
  font-size: 0.9rem;
  line-height: 1.4;
`;

const BubbleMeta = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.15rem;
`;

const AIPreview = () => {
  const { user } = useAuth();
  const hourly = (user?.profile?.hourlyRate || '').trim();
  const shopMinimum = (user?.profile?.shopMinimum || '').trim();
  const halfDay = (user?.profile?.halfDayRate || '').trim();
  const fullDay = (user?.profile?.fullDayRate || '').trim();
  const depositAmount = (user?.profile?.depositAmount || '').trim();
  const workingHours = (user?.profile?.workingHours || '').trim();
  const bookingLink = (user?.profile?.bookingLink || '').trim();
  const preferredStylesList = Array.isArray(user?.profile?.preferredStyles)
    ? user.profile.preferredStyles.filter(Boolean)
    : [];
  const [tone, setTone] = useState('friendly-expert');
  const [clientMessage, setClientMessage] = useState(
    "Hey, I'm interested in a floral forearm tattoo. Do you have availability next month and a rough price range?"
  );
  const [saveMessage, setSaveMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const [generateSuccess, setGenerateSuccess] = useState('');
  const [copyMessage, setCopyMessage] = useState('');
  const [useBookingLink, setUseBookingLink] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('aiPreviewState');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.clientMessage) setClientMessage(parsed.clientMessage);
        if (parsed.assistantDraft) setAssistantDraft(parsed.assistantDraft);
        if (parsed.tone) setTone(parsed.tone);
        if (typeof parsed.useBookingLink === 'boolean') setUseBookingLink(parsed.useBookingLink);
      }
    } catch (e) {
      // ignore corrupted local storage
    }
  }, []);
  const [assistantDraft, setAssistantDraft] = useState(
    "Thanks so much for reaching out! I love floral pieces. I can usually fit forearm tattoos like this into a 2–3 hour session. " +
      "Pricing often starts around $300 and depends on size, detail, and color. " +
      "Share a couple of reference photos and your ideal size, and I can give you a more accurate quote and some time options for next month."
  );

  const handleGenerate = () => {
    setIsGenerating(true);
    setGenerateError('');
    setGenerateSuccess('');

    api
      .post('/ai/preview', {
        tone,
        clientMessage,
        profile: user?.profile || {},
        useBookingLink,
      })
      .then((response) => {
        const draft = response?.data?.draft;
        if (draft) {
          setAssistantDraft(draft);
          setGenerateSuccess('Draft updated from AI. You can edit it before sending.');
        } else {
          setGenerateError('AI did not return a draft.');
        }
      })
      .catch((error) => {
        const message = error?.response?.data?.message || error.message || 'Failed to generate reply.';
        setGenerateError(message);
      })
      .finally(() => {
        setIsGenerating(false);
      });
  };

  const handleReset = () => {
    setClientMessage(
      "Hey, I'm interested in a floral forearm tattoo. Do you have availability next month and a rough price range?"
    );
    setAssistantDraft(
      "Thanks so much for reaching out! I love floral pieces. I can usually fit forearm tattoos like this into a 2–3 hour session. " +
        "Pricing often starts around $300 and depends on size, detail, and color. " +
        "Share a couple of reference photos and your ideal size, and I can give you a more accurate quote and some time options for next month."
    );
    setTone('friendly-expert');
    setSaveMessage('');
    setGenerateError('');
    setGenerateSuccess('');
  };

  const handleSave = () => {
    try {
      const payload = {
        tone,
        clientMessage,
        assistantDraft,
        useBookingLink,
      };
      localStorage.setItem('aiPreviewState', JSON.stringify(payload));
      setSaveMessage('Saved for next time on this device.');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (e) {
      setSaveMessage('Could not save locally.');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const handleCopy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(assistantDraft || '');
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = assistantDraft || '';
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopyMessage('Reply copied to clipboard.');
      setTimeout(() => setCopyMessage(''), 2500);
    } catch (e) {
      setCopyMessage('Could not copy.');
      setTimeout(() => setCopyMessage(''), 2500);
    }
  };

  return (
    <PageContainer>
      <Header>
        <Title>AI Chat Preview</Title>
        <Subtitle>
          Preview how your assistant replies to client DMs, tweak the message, and dial in the tone before anything
          goes live.
        </Subtitle>
        <Subtitle style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
          The AI uses your profile info (styles, pricing, hours, socials) from Account Settings when drafting replies.
        </Subtitle>
      </Header>

      <Layout>
        <Panel>
          <PanelTitle>Client scenario & tone</PanelTitle>
          <div>
            <FieldLabel>
              Artist / studio name <span style={{ fontWeight: 400, fontSize: '0.8rem', color: '#6b7280' }}>
                (edit this in your Account Settings)
              </span>
            </FieldLabel>
            <HelperText>
              {user?.profile?.studioName?.trim() || 'Not set yet'}
            </HelperText>
            {(hourly || shopMinimum || depositAmount || halfDay || fullDay) && (
              <HelperText>
                {hourly && (
                  <>
                    Hourly: {hourly}.{' '}
                  </>
                )}
                {shopMinimum && (
                  <>
                    Shop minimum: {shopMinimum}.{' '}
                  </>
                )}
                {depositAmount && (
                  <>
                    Deposit to book: {depositAmount}.{' '}
                  </>
                )}
                {halfDay && (
                  <>
                    Half-day: {halfDay}.{' '}
                  </>
                )}
                {fullDay && (
                  <>
                    Full-day: {fullDay}.
                  </>
                )}
              </HelperText>
            )}
            {workingHours && (
              <HelperText>Working hours: {workingHours}</HelperText>
            )}
            {preferredStylesList.length > 0 && (
              <HelperText>
                Preferred styles: {preferredStylesList.join(', ')}
              </HelperText>
            )}
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <FieldLabel htmlFor="use-booking-link" style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
              <input
                id="use-booking-link"
                type="checkbox"
                checked={useBookingLink}
                onChange={event => setUseBookingLink(event.target.checked)}
              />
              Use my external booking link in replies
            </FieldLabel>
            <HelperText>
              When this is on, the AI can include your saved booking link from Account Settings instead of sending
              people to the built-in scheduler.
            </HelperText>
            {bookingLink ? (
              <HelperText>
                Current booking link: <span style={{ wordBreak: 'break-all' }}>{bookingLink}</span>
              </HelperText>
            ) : (
              <HelperText>
                No booking link saved yet. Add one in Account Settings under 
                <span style={{ fontWeight: 500 }}> External booking link</span>.
              </HelperText>
            )}
          </div>
          <div>
            <FieldLabel htmlFor="tone">Assistant tone</FieldLabel>
            <Select
              id="tone"
              value={tone}
              onChange={event => setTone(event.target.value)}
            >
              <option value="friendly-expert">Friendly + expert (recommended)</option>
              <option value="minimal">Short & to the point</option>
              <option value="high-touch">High-touch & reassuring</option>
            </Select>
            <HelperText>
              This only changes how the draft is generated here in the preview. Later, this will control the live
              assistant tone. The assistant will use your Artist / studio name from Account Settings when it needs to
              introduce you.
            </HelperText>
          </div>

          <div>
            <FieldLabel htmlFor="client-message">Sample client message</FieldLabel>
            <SmallTextArea
              id="client-message"
              value={clientMessage}
              onChange={event => setClientMessage(event.target.value)}
            />
            <HelperText>
              Use a real DM you often get: placement, size, budget questions, or reschedule requests.
            </HelperText>
          </div>

          <ButtonRow>
            <PrimaryButton type="button" onClick={handleGenerate}>
              {isGenerating ? 'Generating…' : 'Generate draft reply'}
            </PrimaryButton>
            <SecondaryButton type="button" onClick={handleReset}>
              Reset example
            </SecondaryButton>
          </ButtonRow>
          {generateError && (
            <HelperText>
              {generateError}
            </HelperText>
          )}
          {generateSuccess && !generateError && (
            <HelperText style={{ color: '#22c55e' }}>
              {generateSuccess}
            </HelperText>
          )}
          {saveMessage && <HelperText>{saveMessage}</HelperText>}
        </Panel>

        <Panel>
          <PanelTitle>Conversation preview</PanelTitle>

          <ChatBubbleList>
            <BubbleRow $role="user">
              <BubbleMeta>Client DM</BubbleMeta>
            </BubbleRow>
            <BubbleRow $role="user">
              <Bubble $role="user">{clientMessage}</Bubble>
            </BubbleRow>

            <BubbleRow $role="assistant">
              <BubbleMeta>AI assistant draft (you can edit before sending)</BubbleMeta>
            </BubbleRow>
            <BubbleRow $role="assistant">
              <Bubble $role="assistant">{assistantDraft}</Bubble>
            </BubbleRow>
          </ChatBubbleList>

          <div>
            <FieldLabel htmlFor="assistant-draft">Fine-tune this reply</FieldLabel>
            <TextArea
              id="assistant-draft"
              value={assistantDraft}
              onChange={event => setAssistantDraft(event.target.value)}
            />
            <HelperText>
              This is where you shape the exact wording your assistant should use. Later, we can save this as a
              template.
            </HelperText>
            <ButtonRow>
              <PrimaryButton type="button" onClick={handleSave}>
                Save changes
              </PrimaryButton>
              <SecondaryButton type="button" onClick={handleCopy}>
                Copy reply
              </SecondaryButton>
            </ButtonRow>
            {copyMessage && (
              <HelperText style={{ color: '#22c55e' }}>{copyMessage}</HelperText>
            )}
          </div>
        </Panel>
      </Layout>
    </PageContainer>
  );
};

export default AIPreview;
